/**
 * AI — AI Models.
 *
 * Three things happen here:
 *  1. Enable/disable providers (Cloudflare Workers AI natively, or an external API).
 *  2. Curate the model list, tagging each model's modality.
 *  3. Map a data field (newsletter text, prayer requests, image alt text) to the
 *     model that should handle it. The model dropdown for a field only offers
 *     models that can do that kind of work — its own modality plus the multimodal
 *     ones — and the server re-checks on write, so a text field can never end up
 *     pointed at an image generator.
 */
import { useState, useMemo } from 'react'
import { PageHeader } from '../App'
import { api, useApi } from '../lib/api'
import { Picker, PromptSelect, EnumSelect } from '../components/Select'
import ByoProviders from '../components/ByoProviders'
import { MODALITY_HELP, MODALITY_TONE, MODALITY_OPTIONS, modalityFits } from '../lib/modality'
import {
  Card, Field, Badge, Switch, Notice, Modal, Spinner, Tabs, Empty,
  QueryState, useToast, fmtDateTime,
} from '../components/ui'


function FieldMapRow({ row, models, onSaved }) {
  const toast = useToast()
  const [modelId, setModelId] = useState(row.model_id)
  const [promptId, setPromptId] = useState(row.default_prompt_id)
  const [enabled, setEnabled] = useState(!!row.is_enabled)
  const [busy, setBusy] = useState(false)

  const dirty =
    modelId !== row.model_id || promptId !== row.default_prompt_id || enabled !== !!row.is_enabled

  // Only models that can do this field's kind of work are offered — its own
  // modality, plus the multimodal ones, which can do either.
  const options = useMemo(
    () =>
      models
        .filter((m) => modalityFits(m.modality, row.modality))
        .map((m) => ({
          value: m.id,
          label: m.label,
          provider: m.provider_name,
          isDisabled: !m.is_enabled || !m.provider_enabled,
        })),
    [models, row.modality]
  )

  const save = async () => {
    setBusy(true)
    try {
      await api(`settings/llm/field-map/${encodeURIComponent(row.field_key)}`, {
        method: 'PUT',
        body: { model_id: modelId, default_prompt_id: promptId, is_enabled: enabled },
      })
      toast.ok(`${row.label} mapping saved`)
      onSaved()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      title={row.label}
      sub={<span className="mono">{row.field_key}</span>}
      actions={
        <>
          <Badge tone={MODALITY_TONE[row.modality]} title={MODALITY_HELP[row.modality]}>
            {row.modality}
          </Badge>
          <Switch checked={enabled} onChange={setEnabled} title="Allow AI on this field" />
        </>
      }
      footer={
        <>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={!dirty || busy}>
            {busy ? <Spinner /> : 'Save mapping'}
          </button>
          {dirty && <span className="small subtle">Unsaved changes</span>}
          <span className="spacer" />
          {row.updated_at && <span className="tiny subtle">updated {fmtDateTime(row.updated_at)}</span>}
        </>
      }
    >
      <div className="field-row">
        <Field
          label="Model"
          hint={
            options.length
              ? `Only ${row.modality} and multimodal models are listed for this field.`
              : `No ${row.modality} models are available. Add one below.`
          }
        >
          <Picker
            options={options}
            value={modelId}
            onChange={setModelId}
            placeholder="No model assigned"
            formatOptionLabel={(o, meta) => (
              <span className="opt">
                <span className="opt-name">{o.label}</span>
                {meta?.context === 'menu' && (
                  <span className="opt-meta">{o.isDisabled ? 'disabled' : o.provider}</span>
                )}
              </span>
            )}
          />
        </Field>
        <Field
          label="Default prompt"
          hint="Runs when a caller names none. Leave empty and the field's top-priority prompt (AI Settings → Prompts) is used."
        >
          <PromptSelect
            value={promptId}
            onChange={setPromptId}
            targetField={row.field_key}
            onlyTargetField
            placeholder="None — use the top of the priority list"
          />
        </Field>
      </div>
    </Card>
  )
}

function ProviderCard({ provider, models, keyPresent, onSaved }) {
  const toast = useToast()
  const [enabled, setEnabled] = useState(!!provider.is_enabled)
  const [apiBase, setApiBase] = useState(provider.api_base ?? '')
  const [secretName, setSecretName] = useState(provider.secret_name ?? '')
  const [busy, setBusy] = useState(false)

  const isCf = provider.kind === 'cloudflare'
  const dirty =
    enabled !== !!provider.is_enabled ||
    apiBase !== (provider.api_base ?? '') ||
    secretName !== (provider.secret_name ?? '')

  const save = async () => {
    setBusy(true)
    try {
      await api(`settings/llm/providers/${provider.id}`, {
        method: 'PUT',
        body: { is_enabled: enabled, api_base: apiBase, secret_name: secretName },
      })
      toast.ok(`${provider.name} saved`)
      onSaved()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  const counts = models.reduce((acc, m) => {
    acc[m.modality] = (acc[m.modality] || 0) + 1
    return acc
  }, {})

  return (
    <Card
      title={provider.name}
      sub={isCf ? 'Native binding — no API key needed' : provider.api_base}
      actions={
        <>
          {isCf ? (
            <Badge tone="ok">native</Badge>
          ) : keyPresent ? (
            <Badge tone="ok" dot>
              key present
            </Badge>
          ) : (
            <Badge tone="warn" dot>
              key missing
            </Badge>
          )}
          <Switch checked={enabled} onChange={setEnabled} />
        </>
      }
      footer={
        <>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={!dirty || busy}>
            {busy ? <Spinner /> : 'Save'}
          </button>
          <span className="spacer" />
          <span className="small subtle">
            {Object.entries(counts)
              .map(([k, v]) => `${v} ${k}`)
              .join(' · ') || 'no models'}
          </span>
        </>
      }
    >
      {!isCf && (
        <div className="field-row">
          <Field label="API base URL">
            <input className="input" value={apiBase} onChange={(e) => setApiBase(e.target.value)} />
          </Field>
          <Field
            label="Secret name"
            hint={
              keyPresent
                ? 'This secret is set on the Worker.'
                : `Set it with: wrangler secret put ${secretName || 'PROVIDER_API_KEY'}`
            }
          >
            <input className="input mono" value={secretName} onChange={(e) => setSecretName(e.target.value)} />
          </Field>
        </div>
      )}
      {isCf && (
        <p className="small muted">
          Cloudflare Workers AI runs on the account this Worker is deployed to. Inference is billed
          to that account; no key is stored in the database.
        </p>
      )}
      {!isCf && enabled && !keyPresent && (
        <Notice tone="warn" icon="warning" title="Enabled but unusable">
          {provider.name} is switched on but its API key is not set on the Worker, so any field
          mapped to its models will fail at run time.
        </Notice>
      )}
    </Card>
  )
}

function AddModelModal({ open, onClose, providers, onSaved }) {
  const toast = useToast()
  const [providerId, setProviderId] = useState(null)
  const [modelKey, setModelKey] = useState('')
  const [label, setLabel] = useState('')
  const [modality, setModality] = useState('text')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    setBusy(true)
    try {
      await api('settings/llm/models', {
        method: 'POST',
        body: { provider_id: providerId, model_key: modelKey, label: label || modelKey, modality },
      })
      toast.ok('Model added')
      onSaved()
      onClose()
      setModelKey('')
      setLabel('')
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a model"
      footer={
        <>
          <button className="btn btn-primary" onClick={submit} disabled={busy || !providerId || !modelKey.trim()}>
            {busy ? <Spinner /> : 'Add model'}
          </button>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
        </>
      }
    >
      <div className="col">
        <Field label="Provider" required>
          <Picker
            options={providers.map((p) => ({ value: p.id, label: p.name }))}
            value={providerId}
            onChange={setProviderId}
            placeholder="Select a provider…"
          />
        </Field>
        <Field
          label="Model id"
          required
          hint="For Workers AI this is the full model path, e.g. @cf/meta/llama-3.3-70b-instruct-fp8-fast"
        >
          <input
            className="input mono"
            value={modelKey}
            onChange={(e) => setModelKey(e.target.value)}
            placeholder="@cf/…"
          />
        </Field>
        <Field label="Display label">
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={modelKey} />
        </Field>
        <Field label="Modality" required hint={MODALITY_HELP[modality]}>
          <EnumSelect
            value={modality}
            onChange={(v) => setModality(v || 'text')}
            options={MODALITY_OPTIONS}
            isClearable={false}
          />
        </Field>
      </div>
    </Modal>
  )
}

function ModelRow({ model, onSaved }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  const save = async (body, done) => {
    setBusy(true)
    try {
      await api(`settings/llm/models/${model.id}`, { method: 'PUT', body })
      if (done) toast.ok(done)
      onSaved()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <tr>
      <td>
        <div className="semibold">{model.label}</div>
        <div className="tiny subtle mono">{model.model_key}</div>
      </td>
      <td>{model.provider_name}</td>
      <td>
        {/* Editable in place: a model saved as text-only is the reason it never
            appears where a picture has to be read, and re-adding it to change one
            word was the only cure. Generators and embedders are not offered — no
            amount of ticking makes those read a newsletter. */}
        {model.modality === 'image' || model.modality === 'embedding' ? (
          <Badge tone={MODALITY_TONE[model.modality]} title={MODALITY_HELP[model.modality]}>
            {model.modality}
          </Badge>
        ) : (
          <div style={{ minWidth: 170 }} title={MODALITY_HELP[model.modality]}>
            <EnumSelect
              value={model.modality}
              onChange={(v) =>
                v && v !== model.modality && save({ modality: v }, `${model.label} is now ${v}`)
              }
              options={MODALITY_OPTIONS.map((o) => ({ ...o, label: o.value }))}
              isClearable={false}
              isDisabled={busy}
            />
          </div>
        )}
      </td>
      <td className="num">{model.context_window ? model.context_window.toLocaleString() : '—'}</td>
      <td>
        <Switch
          checked={!!model.is_enabled}
          onChange={(on) => save({ is_enabled: on })}
          disabled={busy || !model.provider_enabled}
          title={model.provider_enabled ? 'Enable this model' : 'Its provider is disabled'}
        />
      </td>
    </tr>
  )
}

export default function SettingsLlm({ user }) {
  const query = useApi('settings-llm', 'settings/llm')
  const [tab, setTab] = useState('fields')
  const [addOpen, setAddOpen] = useState(false)
  const refresh = () => query.refetch()

  if (!user.caps.manageSettings) {
    return (
      <PageHeader title="AI Models">
        <Notice tone="warn" icon="lock" title="Not available to your role">
          Only administrators and owners can change AI settings. You are signed in as{' '}
          <strong>{user.roleLabel}</strong>.
        </Notice>
      </PageHeader>
    )
  }

  return (
    <PageHeader
      title="AI Models"
      sub="Choose a provider, curate models, and map each data field to the model that handles it"
      actions={
        <button className="btn btn-sm" onClick={() => setAddOpen(true)}>
          + Add Model
        </button>
      }
    >
      <QueryState query={query}>
        {(data) => (
          <div className="col">
            {!data.aiBindingAvailable && (
              <Notice tone="warn" icon="warning" title="Workers AI binding is not available">
                The <code>AI</code> binding is missing from this deployment, so Cloudflare native
                models cannot run. Check the <code>ai</code> section of wrangler.jsonc.
              </Notice>
            )}
            {!data.imagesBindingAvailable && (
              <Notice tone="info" icon="info" title="Images binding is not available">
                Server-side image resizing needs the <code>IMAGES</code> binding. Until it is
                enabled, uploads rely on the browser-side resize and anything that cannot be
                shrunk below the limit will be rejected rather than stored.
              </Notice>
            )}

            <Tabs
              active={tab}
              onChange={setTab}
              tabs={[
                { key: 'fields', label: 'Field mapping', count: data.fieldMap.length },
                { key: 'providers', label: 'Providers', count: data.providers.length },
                { key: 'models', label: 'Models', count: data.models.length },
              ]}
            />

            {tab === 'fields' && (
              <>
                <Notice icon="info">
                  Each field is restricted to models of a matching modality — a text field will not
                  offer an image model, and the API rejects a mismatched pairing even if the request
                  is crafted by hand.
                </Notice>
                <div className="grid grid-2">
                  {data.fieldMap.map((row) => (
                    <FieldMapRow key={row.field_key} row={row} models={data.models} onSaved={refresh} />
                  ))}
                </div>
              </>
            )}

            {tab === 'providers' && (
              <div className="col">
                <div className="grid grid-2">
                  {data.providers.map((p) => (
                    <ProviderCard
                      key={p.id}
                      provider={p}
                      models={data.models.filter((m) => m.provider_id === p.id)}
                      keyPresent={data.keyStatus[p.key]}
                      onSaved={refresh}
                    />
                  ))}
                </div>
                <ByoProviders />
              </div>
            )}

            {tab === 'models' && (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Model</th>
                      <th>Provider</th>
                      <th>Modality</th>
                      <th className="right">Context</th>
                      <th>Enabled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!data.models.length && (
                      <tr>
                        <td colSpan={5}>
                          <Empty title="No models configured" />
                        </td>
                      </tr>
                    )}
                    {data.models.map((m) => (
                      <ModelRow key={m.id} model={m} onSaved={refresh} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <AddModelModal
              open={addOpen}
              onClose={() => setAddOpen(false)}
              providers={data.providers}
              onSaved={refresh}
            />
          </div>
        )}
      </QueryState>
    </PageHeader>
  )
}
